import React from "react";

// Default avatar values - updated to use hair_type instead of gender
const defaultAvatar = {
  hair_type: "medium", // short, medium, long
  hairColor: "#6D4C41", // brown hair by default
  shirtColor: "#2196F3", // blue shirt by default
  eyeColor: "#795548", // brown eyes by default
  skinColor: "#FFF9C4", // light skin by default
};

const UserAvatar = ({ user, size = 30 }) => {
  // Use user avatar settings or defaults - updated to check both DB names and preview names
  const avatar = {
    hair_type:
      user?.avatar_hair_type || user?.hair_type || defaultAvatar.hair_type,
    hairColor: user?.avatar_hair || user?.hairColor || defaultAvatar.hairColor,
    shirtColor:
      user?.avatar_shirt || user?.shirtColor || defaultAvatar.shirtColor,
    eyeColor: user?.avatar_eyes || user?.eyeColor || defaultAvatar.eyeColor,
    skinColor: user?.avatar_skin || user?.skinColor || defaultAvatar.skinColor,
  };

  // Always render pixel avatar, using provided props or defaults

  // Create an 8-bit style pixelated avatar
  return (
    <div
      style={{
        width: size,
        height: size,
        position: "relative",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      }}
    >
      <style jsx="true">{`
        .avatar-pixel {
          position: absolute;
          width: ${size / 5}px;
          height: ${size / 5}px;
        }
      `}</style>

      {/* Base hair (shared across all hair types) */}
      <div
        className="avatar-pixel"
        style={{
          top: 0,
          left: `${size / 5}px`,
          width: `${(size * 3) / 5}px`,
          backgroundColor: avatar.hairColor,
        }}
      />
      <div
        className="avatar-pixel"
        style={{
          top: `${size / 5}px`,
          left: 0,
          width: `${size}px`,
          backgroundColor: avatar.hairColor,
        }}
      />

      {/* Face */}
      <div
        className="avatar-pixel"
        style={{
          top: `${(size * 2) / 5}px`,
          left: `${size / 5}px`,
          width: `${(size * 3) / 5}px`,
          height: `${(size * 2) / 5}px`,
          backgroundColor: avatar.skinColor,
        }}
      />

      {/* Eyes */}
      <div
        className="avatar-pixel"
        style={{
          top: `${(size * 2) / 5}px`,
          left: `${size / 5}px`,
          width: `${size / 5}px`,
          backgroundColor: avatar.eyeColor,
        }}
      />
      <div
        className="avatar-pixel"
        style={{
          top: `${(size * 2) / 5}px`,
          left: `${(size * 3) / 5}px`,
          width: `${size / 5}px`,
          backgroundColor: avatar.eyeColor,
        }}
      />

      {/* Body/Shirt */}
      <div
        className="avatar-pixel"
        style={{
          top: `${(size * 4) / 5}px`,
          left: `${size / 5}px`,
          width: `${(size * 3) / 5}px`,
          height: `${size / 5}px`,
          backgroundColor: avatar.shirtColor,
        }}
      />

      {/* Hair-type specific styles - changed from gender to hair length */}
      {avatar.hair_type === "short" && (
        <>
          {/* Short hair - only on top and upper sides */}
          <div
            className="avatar-pixel"
            style={{
              top: `${size / 5}px`,
              left: `${size / 5}px`,
              width: `${size / 5}px`,
              height: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          <div
            className="avatar-pixel"
            style={{
              top: `${size / 5}px`,
              left: `${(size * 3) / 5}px`,
              width: `${size / 5}px`,
              height: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          {/* No additional hair pixels below this level */}
        </>
      )}

      {avatar.hair_type === "long" && (
        <>
          {/* Long hair - extends down to shoulders (row 4) */}
          {/* Side hair on row 2 */}
          <div
            className="avatar-pixel"
            style={{
              top: `${(size * 2) / 5}px`,
              left: 0,
              width: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          <div
            className="avatar-pixel"
            style={{
              top: `${(size * 2) / 5}px`,
              left: `${(size * 4) / 5}px`,
              width: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          {/* Side hair on row 3 */}
          <div
            className="avatar-pixel"
            style={{
              top: `${(size * 3) / 5}px`,
              left: 0,
              width: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          <div
            className="avatar-pixel"
            style={{
              top: `${(size * 3) / 5}px`,
              left: `${(size * 4) / 5}px`,
              width: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          {/* Hair at shoulder level on row 4 */}
          <div
            className="avatar-pixel"
            style={{
              top: `${(size * 4) / 5}px`,
              left: 0,
              width: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          <div
            className="avatar-pixel"
            style={{
              top: `${(size * 4) / 5}px`,
              left: `${(size * 4) / 5}px`,
              width: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
        </>
      )}

      {avatar.hair_type === "medium" && (
        <>
          {/* Medium length hair - extends to row 2-3 */}
          {/* Side hair on row 2 */}
          <div
            className="avatar-pixel"
            style={{
              top: `${(size * 2) / 5}px`,
              left: 0,
              width: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          <div
            className="avatar-pixel"
            style={{
              top: `${(size * 2) / 5}px`,
              left: `${(size * 4) / 5}px`,
              width: `${size / 5}px`,
              backgroundColor: avatar.hairColor,
            }}
          />
          {/* No additional hair pixels below this level for medium */}
        </>
      )}
    </div>
  );
};

export default UserAvatar;
